//#region Angular Imports

import { StateService } from '@uirouter/angularjs'

//#endregion

import { Api, LocalDictionaryFactory } from './../api/api.module'

import { ColumnModel } from '../api/api.models'
import { Activity, Enums } from '../../models/common'
import { OpConsumer } from '../../models/core'

import { BaseGridController, ScopeBaseGrid } from '../common/common.base.grid.controller'
import { ButtonsOptions } from '../common/grid/grid.controller'
import { CreateEditInitParams, BulkEditInitParams } from '../common/create-edit-popup/create-edit-popup.controller'
import { DetailsOptions } from '../common/details-popup/details-popup.directive'
import { GridService, GridContextMenuService } from '../../services/grid'
import { GridController, GridTypes } from '../common/grid/grid.controller'
import { LoggerService } from './../common/common.module'

import { CommonHelper, EventsNames, IUserSession, LocalizationHelper, TypeHelper } from '../../helpers'
import { SystemService } from '../../services/system'

import { ConsumerHelper } from './consumers.helper'

export interface ConsumerScope extends ScopeBaseGrid {
    deleteConfirmationOptions: {
        title: string
        onConfirmed: () => void
    }
}

export class ConsumerController extends BaseGridController {

    // #region Consts

    private get GRID_NAME() { return 'ConsumersGrid' }

    // #endregion


    // # region Members

    private grid: GridController<OpConsumer>

    // #endregion


    // #region Ctor

    constructor(
        protected $scope: ConsumerScope,
        $state: StateService,
        $timeout: ng.ITimeoutService,
        api: Api,
        private logger: LoggerService,
        localDictionary: LocalDictionaryFactory,
        private gridContextMenuService: GridContextMenuService,
        protected userSession: IUserSession,
        private gridService: GridService,
        protected systemService: SystemService,
        referenctType: Enums.ReferenceType,
        private moduleName: Enums.SimaxModuleName
    ) {
        super($scope, $timeout, $state, userSession, systemService, api, localDictionary, null, referenctType)

        this.setConfirmationsOptions()
        this.setEvents()
        this.setMainGridOptions()
    }

    // #endregion


    // #region Events

    private onInitialized(e: any, grid: GridController<OpConsumer>): void {
        this.grid = grid
        this.dataGrid = e.component

        this.setGridId(e.tableId)
    }


    private onAdd(): void {
        const createEditInitParams: CreateEditInitParams = ConsumerHelper.getCreateEditOptions()
        this.$scope.$broadcast(EventsNames.INITIALIZE_CREATE_EDIT_POPUP, createEditInitParams)
    }

    private onEdit(): void {
        if (this.dataGrid.getSelectedRowKeys().length == 1) {
            const createEditInitParams: CreateEditInitParams = ConsumerHelper.getCreateEditOptions(this.grid.selectedItemId)
            this.$scope.$broadcast(EventsNames.INITIALIZE_CREATE_EDIT_POPUP, createEditInitParams)
        } else {
            const columnCount: number = this.dataGrid.columnCount()
            const columns: any[] = []

            for (let i = 0; i < columnCount; i++) {
                columns.push(this.dataGrid.columnOption(i))
            }

            const bulkEditInitParams: BulkEditInitParams = {
                gridColumns: columns,
                groupReferenceType: null,
                groupReferenceValue: null,
                idReferenceType: this.referenceType,
                moduleName: this.moduleName,
                section: null,
                selectedGridRows: TypeHelper.clone(this.grid.selectedGridItems, true),
                useBusinessObjects: true
            }

            this.$scope.$root.$broadcast(EventsNames.INITIALIZE_BULK_EDIT_POPUP, bulkEditInitParams)
        }
    }

    private onShowDetails(e: any): void {
        const detailsOptions: DetailsOptions = {
            hasPermission: true,
            idReferenceType: this.referenceType,
            moduleName: this.moduleName,
            pageItemsIds: this.grid.pageItemsIds,
            referenceObjectId: this.grid.selectedItemId
        }

        this.showDetails(e, detailsOptions)
    }

    private onDelete(): void {
        this.$scope.$broadcast(
            EventsNames.OPEN_CONFIRMATION_POPUP + this.$scope.popupId,
            LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__CONFIRM_DELETE, this.moduleName)
        )
    }

    private onDeleted(): void {
        this.grid.dxGrid.refresh()
    }

    private onConfirmDelete(): void {
        ConsumerHelper
            .deleteConsumer(this.userSession.getDataProvider(), this.grid.selectedItemId, this.moduleName)
            .then(() => {
                this.$scope.$root.$broadcast(EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__DELETED, this.moduleName))
            })
            .fail((error: any) => {
                this.logger.log(error)
            })
    }

    // #endregion

    private getGridButtonsOptions(): ButtonsOptions {
        return {
            enabled: true,
            addPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.CONSUMER_ADD) ? "['" + Activity[Activity.CONSUMER_ADD] + "']" : null,
            deletePermissions: this.userSession.getLoggedOperator().hasPermission(Activity.CONSUMER_DELETE) ? "['" + Activity[Activity.CONSUMER_DELETE] + "']" : null,
            editPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.CONSUMER_EDIT) ? "['" + Activity[Activity.CONSUMER_EDIT] + "']" : null,
            saveColumnSettingsPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.WEBSIMAX_MODULE_VIEW_COLUMN_DEFINITION_OP) ? "['" + Activity[Activity.WEBSIMAX_MODULE_VIEW_COLUMN_DEFINITION_OP] + "']" : null,
            showDetailsPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.CONSUMER_LIST) ? "['" + Activity[Activity.CONSUMER_LIST] + "']" : null,
            onAddClick: () => this.onAdd(),
            onDeleteClick: () => this.onDelete(),
            onEditClick: () => this.onEdit(),
            onShowClick: (e: any) => this.onShowDetails(e),
        }
    }

    private getContextMenuItems(isTouchScreen: boolean = false): any[] {
        const items = this.gridContextMenuService.getContextMenuItemsByReferenceType(Enums.ReferenceType.IdConsumer, {
            idReferenceTypes: this.grid.selectedItemsIds,
            onEdit: () => this.onEdit(),
        })

        return this.mergeWithDefaultItems(isTouchScreen, items);
    }

    private setConfirmationsOptions(): void {
        this.$scope.deleteConfirmationOptions = {
            title: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETING, this.moduleName),
            onConfirmed: () => this.onConfirmDelete()
        }
    }

    private setEvents(): void {
        this.$scope.$on(
            EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__DELETED, this.moduleName),
            (event: ng.IAngularEvent, ...args: any[]) => this.onDeleted()
        )
        this.$scope.showDetails = (e: any) => this.onShowDetails(e)
    }

    private setMainGridOptions(): void {
        this.gridService
            .getColumnModelByCategory([
                Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__GRID_PARAMS,
                Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__DEFAULT_GRID_PARAMS
            ], this.moduleName)
            .then((columns: ColumnModel[], defaultColumns: ColumnModel[]) => {
                this.$scope.mainGridOptions = {
                    referenceId: this.referenceType,
                    idReferenceType: this.referenceType,
                    type: GridTypes.MAIN,
                    name: this.GRID_NAME,
                    addCustomFilters: true,
                    buttonsOptions: this.getGridButtonsOptions(),
                    columnsDictionary: columns,
                    dataSourceAdditionalFields: null,
                    defaultColumnsDictionary: defaultColumns,
                    localStorageVarForSavedEntity: CommonHelper.localStorageVarForSavedEntity.consumerSaved,
                    preExecuteClearFilter: this.preExecuteGridClearFilter,
                    allowColumnReordering: !this.userSession
                        .getLoggedOperator()
                        .hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.ConsumerModuleMainGrid),
                    getDataSourceMethod: this.userSession
                        .getDataProvider().Consumer.getConsumerFilter,
                    onInitialized: (e: any, grid: GridController<OpConsumer>) => this.onInitialized(e, grid),
                    onDataReady: (data: any) => this.onDataReady(data),
                    onRowDoubleClick: (e: any) => this.onShowDetails(e),
                    contextMenuItems: () => this.getContextMenuItems()
                }
            })
    }
}